import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Church, Check, X, Trash2, Edit, Search, ImagePlus, Loader2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: "pending", label: "Pendentes" },
  { key: "approved", label: "Aprovados" },
  { key: "rejected", label: "Rejeitados" },
  { key: "all", label: "Todos" },
];

export default function AdminShrines() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/shrines`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Erro a carregar santuários.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const lc = q.toLowerCase();
    return items.filter((s) => {
      if (tab !== "all" && s.status !== tab) return false;
      if (!q) return true;
      return [s.name, s.host_company, s.city].some((x) => String(x ?? "").toLowerCase().includes(lc));
    });
  }, [items, q, tab]);

  const counts = useMemo(() => ({
    pending: items.filter((s) => s.status === "pending").length,
    approved: items.filter((s) => s.status === "approved").length,
    rejected: items.filter((s) => s.status === "rejected").length,
    all: items.length,
  }), [items]);

  const approve = async (id) => {
    if (!window.confirm("Aprovar este santuário? Ficará visível na área de sócios.")) return;
    try {
      await axios.post(`${API}/admin/shrines/${id}/approve`, {}, { withCredentials: true });
      toast.success("Santuário aprovado.");
      load();
    } catch (e) { toast.error("Erro a aprovar."); }
  };

  const reject = async (id) => {
    if (!window.confirm("Rejeitar este santuário?")) return;
    try {
      await axios.post(`${API}/admin/shrines/${id}/reject`, {}, { withCredentials: true });
      toast.success("Santuário rejeitado.");
      load();
    } catch (e) { toast.error("Erro a rejeitar."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar definitivamente este santuário?")) return;
    try {
      await axios.delete(`${API}/admin/shrines/${id}`, { withCredentials: true });
      toast.success("Santuário eliminado.");
      load();
    } catch (e) { toast.error("Erro a eliminar."); }
  };

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-14" data-testid="admin-shrines-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline">Santuários</div>
          <h1 className="display-heading text-4xl sm:text-5xl mt-3">Santuários Empreendedores.</h1>
          <span className="accent-rule mt-6 block" />
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] max-w-xl">
            Geste as propostas dos sócios e os santuários activos nas empresas.
          </p>
        </div>
        <button
          onClick={() => setEditing({ __new: true })}
          className="btn-primary"
          data-testid="admin-shrines-new"
        >
          <Church className="h-4 w-4" /> Criar santuário
        </button>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-xs uppercase tracking-[0.18em] px-4 py-2 border ${
              tab === t.key
                ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white"
                : "border-[hsl(var(--border))] bg-white text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
            }`}
            data-testid={`admin-shrines-tab-${t.key}`}
          >
            {t.label} <span className="ml-1.5 opacity-70">({counts[t.key]})</span>
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Procurar…"
            className="pl-9 pr-3 py-2 bg-white border border-[hsl(var(--border))] text-sm w-56 focus:outline-none focus:border-[hsl(var(--accent))]"
            data-testid="admin-shrines-search"
          />
        </div>
      </div>

      <div className="mt-6 bg-white border border-[hsl(var(--border))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
              <Th>Foto</Th>
              <Th>Nome</Th>
              <Th>Empresa</Th>
              <Th>Cidade</Th>
              <Th>Inauguração</Th>
              <Th>Responsável</Th>
              <Th>Assessor</Th>
              <Th>Estado</Th>
              <th className="text-right text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] font-medium px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">A carregar…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-[hsl(var(--muted-foreground))]">Nenhum santuário neste estado.</td></tr>
            ) : (
              filtered.map((s) => (
                <tr key={s.id} className="border-b border-[hsl(var(--border))] last:border-0" data-testid={`admin-shrine-row-${s.id}`}>
                  <td className="px-4 py-3 align-top">
                    {s.photo_url ? (
                      <img src={s.photo_url} alt="" className="h-12 w-16 object-cover" />
                    ) : (
                      <div className="h-12 w-16 bg-[hsl(var(--secondary))] flex items-center justify-center">
                        <Church className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top font-medium">{s.name}</td>
                  <td className="px-4 py-3 align-top">{s.host_company}</td>
                  <td className="px-4 py-3 align-top">{s.city || "—"}</td>
                  <td className="px-4 py-3 align-top">{s.inauguration_date || "—"}</td>
                  <td className="px-4 py-3 align-top">{s.responsible_member_name || "—"}</td>
                  <td className="px-4 py-3 align-top">
                    {s.assessor_member_name ? (
                      <div className="text-xs">
                        <div>{s.assessor_member_name}</div>
                        {s.assessor_decision && (
                          <div className={`mt-0.5 text-[10px] uppercase tracking-[0.16em] ${
                            s.assessor_decision === "validated"
                              ? "text-[hsl(var(--primary))]"
                              : "text-red-700"
                          }`}>
                            {s.assessor_decision === "validated" ? "✓ Validou" : "✗ Rejeitou"}
                          </div>
                        )}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <StatusBadge status={s.status} />
                    {s.approved_by_kind && s.status === "approved" && (
                      <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]">
                        por {s.approved_by_kind === "assessor" ? "Assessor" : "Direção"}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right align-top">
                    <div className="inline-flex gap-1 flex-wrap justify-end">
                      {s.status === "pending" && (
                        <>
                          <ActionBtn onClick={() => approve(s.id)} testid={`approve-${s.id}`} title="Aprovar" tone="primary">
                            <Check className="h-3.5 w-3.5" />
                          </ActionBtn>
                          <ActionBtn onClick={() => reject(s.id)} testid={`reject-${s.id}`} title="Rejeitar" tone="muted">
                            <X className="h-3.5 w-3.5" />
                          </ActionBtn>
                        </>
                      )}
                      <ActionBtn onClick={() => setEditing(s)} testid={`edit-${s.id}`} title="Editar">
                        <Edit className="h-3.5 w-3.5" />
                      </ActionBtn>
                      <ActionBtn onClick={() => remove(s.id)} testid={`delete-${s.id}`} title="Eliminar" tone="danger">
                        <Trash2 className="h-3.5 w-3.5" />
                      </ActionBtn>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ShrineFormDialog
          shrine={editing.__new ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] font-medium px-4 py-3">{children}</th>
);

const ActionBtn = ({ children, onClick, testid, title, tone = "default" }) => {
  const toneClass = tone === "primary"
    ? "bg-[hsl(var(--primary))] text-white hover:bg-[#093630]"
    : tone === "danger"
      ? "border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-red-700 hover:border-red-700"
      : tone === "muted"
        ? "border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]"
        : "border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]";
  return (
    <button onClick={onClick} title={title} className={`inline-flex items-center gap-1 px-2 py-1.5 ${toneClass}`} data-testid={testid}>
      {children}
    </button>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: "Pendente", klass: "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]" },
    approved: { label: "Aprovado", klass: "bg-[hsl(var(--primary))] text-white" },
    rejected: { label: "Rejeitado", klass: "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] line-through" },
  };
  const cfg = map[status] || map.pending;
  return <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 ${cfg.klass}`}>{cfg.label}</span>;
};

function ShrineFormDialog({ shrine, onClose, onSaved }) {
  const isEdit = !!shrine?.id;
  const [form, setForm] = useState({
    name: shrine?.name || "",
    host_company: shrine?.host_company || "",
    address: shrine?.address || "",
    city: shrine?.city || "",
    country: shrine?.country || "Portugal",
    inauguration_date: shrine?.inauguration_date || "",
    description_pt: shrine?.description_pt || "",
    description_en: shrine?.description_en || "",
    description_es: shrine?.description_es || "",
    photo_url: shrine?.photo_url || "",
    responsible_member_id: shrine?.responsible_member_id || "",
    community_id: shrine?.community_id || "",
    active: shrine?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [members, setMembers] = useState([]);
  const [communities, setCommunities] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, c] = await Promise.all([
          axios.get(`${API}/admin/members`, { withCredentials: true }),
          axios.get(`${API}/admin/communities`, { withCredentials: true }),
        ]);
        setMembers((m.data?.items || []).filter((mm) => mm.status === "approved"));
        setCommunities((c.data?.items || []).filter((cc) => cc.active));
      } catch { toast.error("Erro a carregar sócios e comunidades."); }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await axios.post(`${API}/admin/shrines/upload-image`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_url", r.data.url);
      toast.success("Imagem carregada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar imagem.");
    } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.host_company.trim()) {
      toast.error("Preencha nome e empresa anfitriã.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip empties so PATCH doesn't unset accidentally on the backend
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (isEdit) {
        await axios.patch(`${API}/admin/shrines/${shrine.id}`, payload, { withCredentials: true });
        toast.success("Santuário actualizado.");
      } else {
        await axios.post(`${API}/admin/shrines`, payload, { withCredentials: true });
        toast.success("Santuário criado.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 overflow-y-auto" data-testid="admin-shrine-dialog">
      <form onSubmit={save} className="bg-white max-w-2xl w-full border border-[hsl(var(--border))] my-8">
        <div className="px-7 py-6 border-b border-[hsl(var(--border))] flex items-start justify-between">
          <div>
            <div className="overline">Santuários</div>
            <h3 className="display-heading text-2xl mt-2">{isEdit ? "Editar santuário" : "Criar santuário"}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] -mr-2 p-1" data-testid="admin-shrine-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-7 py-6 space-y-5">
          {/* Photo */}
          <div className="flex items-center gap-5">
            <div className="h-24 w-32 bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] flex items-center justify-center overflow-hidden">
              {form.photo_url ? (
                <img src={form.photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Church className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />
              )}
            </div>
            <div>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="btn-outline disabled:opacity-60" data-testid="admin-shrine-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {uploading ? "A carregar…" : "Carregar foto"}
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                     onChange={(e) => upload(e.target.files?.[0])} className="hidden"
                     data-testid="admin-shrine-upload-input" />
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">JPG, PNG ou WEBP. Máx 5 MB.</p>
            </div>
          </div>

          <FInput label="Nome do santuário *" value={form.name} onChange={(v) => set("name", v)} testid="adm-name" required />
          <FInput label="Empresa anfitriã *" value={form.host_company} onChange={(v) => set("host_company", v)} testid="adm-host" required />
          <div className="grid sm:grid-cols-2 gap-4">
            <FInput label="Morada" value={form.address} onChange={(v) => set("address", v)} testid="adm-address" />
            <FInput label="Cidade" value={form.city} onChange={(v) => set("city", v)} testid="adm-city" />
            <FInput label="País" value={form.country} onChange={(v) => set("country", v)} testid="adm-country" />
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Data de inauguração</label>
              <input type="date" value={form.inauguration_date} onChange={(e) => set("inauguration_date", e.target.value)}
                     className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                     data-testid="adm-date" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Sócio responsável</label>
              <select value={form.responsible_member_id || ""} onChange={(e) => set("responsible_member_id", e.target.value)}
                      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                      data-testid="adm-responsible">
                <option value="">— sem responsável —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} — {m.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Comunidade</label>
              <select value={form.community_id || ""} onChange={(e) => set("community_id", e.target.value)}
                      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                      data-testid="adm-community">
                <option value="">— nacional —</option>
                {communities.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <FTextarea label="Descrição (PT)" value={form.description_pt} onChange={(v) => set("description_pt", v)} testid="adm-desc-pt" />
          <FTextarea label="Descrição (EN)" value={form.description_en} onChange={(v) => set("description_en", v)} testid="adm-desc-en" />
          <FTextarea label="Descrição (ES)" value={form.description_es} onChange={(v) => set("description_es", v)} testid="adm-desc-es" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="adm-active" />
            Santuário activo (visível na área de sócios)
          </label>
        </div>
        <div className="px-7 py-5 bg-[hsl(var(--secondary))] flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="admin-shrine-cancel">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="admin-shrine-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "A guardar…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

const FInput = ({ label, value, onChange, testid, required }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">{label}</label>
    <input type="text" value={value || ""} required={!!required}
           onChange={(e) => onChange(e.target.value)}
           className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
           data-testid={testid} />
  </div>
);

const FTextarea = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">{label}</label>
    <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
              className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid={testid} />
  </div>
);
